type VoiceInputControllerOptions = {
  input: HTMLTextAreaElement;
  button: HTMLElement | null;
  autoResize: () => void;
  onInputUpdated: () => void;
  showToast: (message: string) => void;
};

function getSpeechRecognitionCtor() {
  const speechWindow = window as Window & {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
  };
  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
}

function supportsSpeechRecognition() {
  return typeof getSpeechRecognitionCtor() === 'function';
}

function mergeSpeechInputText(base = '', committed = '', interim = '') {
  const baseText = String(base || '');
  const extraText = [String(committed || '').trim(), String(interim || '').trim()].filter(Boolean).join(' ');
  if (!extraText) return baseText;
  if (!baseText.trim()) return extraText;
  const separator = /[\s\n]$/.test(baseText) ? '' : '\n';
  return `${baseText}${separator}${extraText}`;
}

export function createVoiceInputController(options: VoiceInputControllerOptions) {
  let recognition: any = null;
  let active = false;
  let committedText = '';
  let interimText = '';
  let baseText = '';

  function applySpeechInputText() {
    options.input.value = mergeSpeechInputText(baseText, committedText, interimText);
    options.autoResize();
    options.onInputUpdated();
  }

  function updateButton() {
    if (!options.button) return;
    const supported = supportsSpeechRecognition();
    (options.button as HTMLButtonElement).disabled = !supported;
    (options.button as HTMLElement).hidden = !supported;
    options.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    options.button.classList.toggle('recording', active);
    (options.button as HTMLElement).title = supported
      ? (active ? '停止语音输入' : '语音输入')
      : '当前浏览器不支持语音输入';
  }

  function stop(stopOptions: any = {}) {
    const current = recognition;
    recognition = null;
    active = false;
    interimText = '';
    if (!stopOptions.keepText) {
      committedText = '';
      baseText = options.input.value || '';
    }
    if (current && typeof current.stop === 'function') {
      try { current.stop(); } catch {}
    }
    updateButton();
    if (stopOptions.refocus !== false) options.input.focus();
    options.onInputUpdated();
  }

  function start() {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) {
      options.showToast('当前浏览器不支持语音输入');
      updateButton();
      return;
    }
    if (active) return;

    committedText = '';
    interimText = '';
    baseText = options.input.value || '';

    recognition = new SpeechRecognitionCtor();
    active = true;
    updateButton();
    recognition.lang = navigator.language || 'zh-CN';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      let nextCommitted = committedText;
      let nextInterim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = String(result?.[0]?.transcript || '').trim();
        if (!transcript) continue;
        if (result.isFinal) nextCommitted = `${nextCommitted} ${transcript}`.trim();
        else nextInterim = `${nextInterim} ${transcript}`.trim();
      }
      committedText = nextCommitted;
      interimText = nextInterim;
      applySpeechInputText();
    };

    recognition.onerror = (event: any) => {
      const error = String(event?.error || '');
      if (error === 'not-allowed' || error === 'service-not-allowed') {
        options.showToast('语音输入权限被拒绝，请在浏览器中允许麦克风访问');
      } else if (error === 'no-speech') {
        options.showToast('没有识别到语音，请重试');
      } else if (error && error !== 'aborted') {
        options.showToast(`语音输入失败：${error}`);
      }
    };

    recognition.onend = () => {
      recognition = null;
      active = false;
      interimText = '';
      applySpeechInputText();
      updateButton();
      options.input.focus();
    };

    try {
      recognition.start();
      options.showToast('语音输入已开始');
    } catch {
      recognition = null;
      active = false;
      updateButton();
      options.showToast('语音输入启动失败，请重试');
    }
  }

  return {
    isActive() {
      return active;
    },
    updateButton,
    stop,
    toggle() {
      if (active) {
        stop({ keepText: true });
        options.showToast('语音输入已停止');
        return;
      }
      start();
    },
  };
}
